import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IApplicationFormData,
  IFieldArrayData,
  IFieldData,
  IFieldGroupNameData,
} from '../../ui/models/application-form-data.model';
import { TDoubleIdentityFormControlValue } from '../../ui/models/citizen-document-types.enum';
import {
  TableWithInputColumn,
  resolveTableWithInputColumnKind,
  resolveTableWithInputSelectDisplayValues,
} from '../../ui/models/irembo-table-with-input.model';

interface IGetDatFieldsData {
  fieldGroup: IFieldData[];
  fieldArray?: IFieldArrayData[];
}

export const applicationFormBuildPreviewData = (
  layout: FormlyFieldConfig[],
  layoutData: Record<string, unknown>,
  addLabelCount = false
): IApplicationFormData[] => {
  if (!layout[0].fieldGroup) {
    return [];
  }

  return layout[0].fieldGroup
    .filter(section => !section.hide || section?.props?.['hideFromPreview'])
    .map(section => {
      const sectionName = section?.props?.label as string;
      const sectionKey = section?.key as string;
      const formGroup: IFieldGroupNameData[] = [];
      section.fieldGroup?.forEach(block => {
        if (block.props?.['hideFromPreview']) {
          return;
        }
        const fieldGroupName = block?.props?.label as string;
        const fieldGroupKey = block?.key as string;
        const fieldGroupData: IGetDatFieldsData = getFieldData(
          section,
          block,
          layoutData,
          addLabelCount
        );

        if (
          fieldGroupData.fieldGroup.length > 0 ||
          fieldGroupData?.fieldArray?.length
        ) {
          formGroup.push({
            fieldGroupName,
            fieldGroupKey,
            fieldGroup:
              fieldGroupData.fieldGroup.length > 0
                ? fieldGroupData.fieldGroup
                : undefined,
            fieldArray:
              fieldGroupData?.fieldArray?.length &&
              fieldGroupData.fieldArray.length > 0
                ? fieldGroupData.fieldArray
                : undefined,
          });
        }
      });
      return {
        formGroupName: sectionName,
        formGroupKey: sectionKey,
        formGroup,
      };
    })
    .filter(x => x.formGroup.length > 0);
};

const getFieldData = (
  section: FormlyFieldConfig,
  block: FormlyFieldConfig,
  layoutData: Record<string, unknown>,
  addLabelCount = false,
  isNestedBlock = false
): IGetDatFieldsData => {
  const fieldGroup: IFieldData[] = [];
  let fieldArray: IFieldArrayData[] | undefined = undefined;

  // Collect source fields from custommultiselectdatafetch previewMetadata to hide them
  const hiddenSourceFields = new Set<string>();
  block.fieldGroup?.forEach(field => {
    if (field.type === 'custommultiselectdatafetch') {
      const previewMetadata = field.props?.['previewMetadata'] || [];
      previewMetadata.forEach((config: any) => {
        if (config.type === 'input' && config.sourceField) {
          hiddenSourceFields.add(config.sourceField);
        }
      });
    }
  });

  block.fieldGroup?.forEach((field, order) => {
    if (field.props?.['hideFromPreview']) {
      return;
    }

    // Skip fields that are used as source in custommultiselectdatafetch previewMetadata
    if (hiddenSourceFields.has(field.key as string)) {
      return;
    }

    let value = (
      (layoutData?.[section?.key as string] as Record<string, unknown>)?.[
        block?.key as string
      ] as Record<string, unknown>
    )?.[field.key as string];

    if (isNestedBlock) {
      value = (layoutData?.[block?.key as string] as Record<string, unknown>)?.[
        field?.key as string
      ];
    }

    if (!value) {
      return;
    }

    switch (field.type) {
      case 'customcascadingdropdowns':
        field?.props?.['configs'].forEach((ele: Record<string, unknown>) => {
          if ((value as Record<string, unknown>)[ele['key'] as string]) {
            fieldGroup.push({
              fieldType: 'customdropdown',
              fieldBindLabel: ele['bindlabel'],
              fieldName: ele['key'] as string,
              fieldLabel: ele['label'] as string,
              fieldParentKey: field['key'] as string,
              fieldValue: (value as Record<string, unknown>)[
                ele['key'] as string
              ],
              order,
            });
          }
        });
        break;
      case 'customslotbooking':
        field?.props?.['configs'].forEach((ele: Record<string, unknown>) => {
          if ((value as Record<string, unknown>)[ele['key'] as string]) {
            if (ele['isSeats']) {
              fieldGroup.push({
                fieldType: 'number',
                fieldBindLabel: '',
                fieldName: ele['key'] as string,
                fieldLabel: ele['label'] as string,
                fieldParentKey: field['parent'] as string,
                fieldValue: (value as Record<string, unknown>)[
                  ele['key'] as string
                ],
                order,
              });
            } else {
              fieldGroup.push({
                fieldType: 'customdropdown',
                fieldBindLabel: ele['bindlabel'],
                fieldName: ele['key'] as string,
                fieldLabel: ele['label'] as string,
                fieldParentKey: field['parent'] as string,
                fieldValue: (value as Record<string, unknown>)[
                  ele['key'] as string
                ],
                order,
              });
            }
          }
        });
        break;
      case 'customdoubleidinput':
        Object.keys(<TDoubleIdentityFormControlValue>value).forEach(key => {
          if ((<TDoubleIdentityFormControlValue>value)[key]) {
            fieldGroup.push({
              fieldType: 'customdoubleidinput',
              fieldBindLabel: '',
              fieldName: key,
              fieldLabel:
                (<TDoubleIdentityFormControlValue>value)[key]?.idLabel ?? '',
              fieldParentKey: field['key'] as string,
              fieldValue: (<TDoubleIdentityFormControlValue>value)[key],
              order,
            });
          }
        });
        break;
      case 'customrepeater': {
        const customRepeaterField: IFieldArrayData = {
          fieldArrayKey: field.key as string,
          arrayItems: [],
          order,
        };

        const fieldArrayGroup: FormlyFieldConfig[] =
          (<FormlyFieldConfig>field.fieldArray)?.fieldGroup ?? [];

        if (fieldArrayGroup.length > 0 && typeof value === 'object') {
          customRepeaterField.arrayItems = Object.keys(value).map(
            (_key, index: number) => {
              const fieldGroup: IFieldData[] = [];
              const formGroup: IFieldGroupNameData[] = [];
              const repeaterFieldValue = (<Record<string, unknown>>value)[
                index
              ];
              fieldArrayGroup.forEach((repeaterField: FormlyFieldConfig) => {
                switch (repeaterField.type) {
                  case 'block': {
                    const formGroupData = getNestedFields(
                      repeaterField,
                      addLabelCount,
                      index,
                      section,
                      repeaterFieldValue as Record<string, unknown>,
                      index
                    );
                    if (formGroupData) {
                      formGroup.push(formGroupData);
                    }
                    break;
                  }
                  case 'customcascadingdropdowns': {
                    fieldGroup.push(
                      ...createLocationFields(
                        repeaterField?.props?.['configs'],
                        repeaterFieldValue,
                        repeaterField,
                        repeaterField['key'] as string,
                        index
                      )
                    );
                    break;
                  }
                  case 'customdatatable': {
                    buildCustomDatatablePreviewData(
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ],
                      fieldGroup,
                      repeaterField,
                      index
                    );
                    break;
                  }
                  case 'customtablewithinputform': {
                    buildCustomTableWithInputPreviewData(
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ],
                      fieldGroup,
                      repeaterField,
                      index
                    );
                    break;
                  }
                  default: {
                    if (
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ]
                    ) {
                      fieldGroup.push({
                        fieldType: repeaterField.type as string,
                        fieldBindLabel:
                          (repeaterField?.props?.['bindLabel'] as string) ?? '',
                        fieldName: repeaterField.key as string,
                        fieldLabel: (repeaterField?.props?.label +
                          setCountOnLabel(addLabelCount, index)) as string,
                        fieldValue: (<Record<string, unknown>>(
                          repeaterFieldValue
                        ))[repeaterField.key as string],
                        order: index,
                      });
                    }
                    break;
                  }
                }
              });
              return {
                fieldGroup,
                formGroup,
              };
            }
          );
        }

        if (!fieldArray) {
          fieldArray = [customRepeaterField];
          break;
        }

        fieldArray.push(customRepeaterField);
        break;
      }
      case 'customdynamicrepeater': {
        const customDynamicRepeaterField: IFieldArrayData = {
          fieldArrayKey: field.key as string,
          arrayItems: [],
          order,
        };

        const fieldArrayGroup: FormlyFieldConfig[] =
          (<FormlyFieldConfig>field.fieldArray)?.fieldGroup ?? [];

        if (
          fieldArrayGroup.length > 0 &&
          typeof value === 'object' &&
          Array.isArray(value)
        ) {
          // Get the dynamic repeater configuration
          const dynamicConfig = field.props?.['dynamicRepeaterConfig'] || {};
          const itemIdField = dynamicConfig.itemIdField || 'id';
          const itemLabelField = dynamicConfig.itemLabelField || 'label';

          // Get items data from props if available (for API-populated items)
          const itemsData = field.props?.['items'] || [];

          customDynamicRepeaterField.arrayItems = value.map(
            (repeaterFieldValue: any, index: number) => {
              const fieldGroup: IFieldData[] = [];
              const formGroup: IFieldGroupNameData[] = [];

              // Get the corresponding item data for dynamic labels
              const itemData = itemsData[index] || {};
              const itemLabel =
                getNestedValue(itemData, itemLabelField) || `Item ${index + 1}`;
              const itemId = getNestedValue(itemData, itemIdField);

              // Create a context for label interpolation
              const labelContext = {
                itemLabel,
                itemId,
                label: itemLabel,
                id: itemId,
                ...itemData, // Include all item data for flexible templating
              };

              fieldArrayGroup.forEach((repeaterField: FormlyFieldConfig) => {
                // Skip hidden fields like itemId and _isApiPopulated
                if (
                  repeaterField.props?.['hideField'] ||
                  repeaterField.key === 'itemId' ||
                  repeaterField.key === '_isApiPopulated'
                ) {
                  return;
                }

                switch (repeaterField.type) {
                  case 'block': {
                    // For nested blocks, we need to pass the label context
                    const formGroupData = getNestedFieldsWithContext(
                      repeaterField,
                      addLabelCount,
                      index,
                      section,
                      repeaterFieldValue as Record<string, unknown>,
                      index,
                      labelContext
                    );
                    if (formGroupData) {
                      formGroup.push(formGroupData);
                    }
                    break;
                  }
                  case 'customcascadingdropdowns': {
                    fieldGroup.push(
                      ...createLocationFields(
                        repeaterField?.props?.['configs'],
                        repeaterFieldValue,
                        repeaterField,
                        repeaterField['key'] as string,
                        index
                      )
                    );
                    break;
                  }
                  default: {
                    if (
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ]
                    ) {
                      // Interpolate the label with the dynamic context
                      const interpolatedLabel = interpolateLabel(
                        repeaterField?.props?.label as string,
                        labelContext
                      );

                      fieldGroup.push({
                        fieldType: repeaterField.type as string,
                        fieldBindLabel:
                          (repeaterField?.props?.['bindLabel'] as string) ?? '',
                        fieldName: repeaterField.key as string,
                        fieldLabel: interpolatedLabel,
                        fieldValue: (<Record<string, unknown>>(
                          repeaterFieldValue
                        ))[repeaterField.key as string],
                        order: index,
                      });
                    }
                    break;
                  }
                }
              });
              return {
                fieldGroup,
                formGroup,
              };
            }
          );
        }

        if (!fieldArray) {
          fieldArray = [customDynamicRepeaterField];
          break;
        }

        fieldArray.push(customDynamicRepeaterField);
        break;
      }
      case 'customdatatable': {
        buildCustomDatatablePreviewData(value, fieldGroup, field, order);
        break;
      }
      case 'customitemselectiontable': {
        // Same as customdatatable - just display the value as a table
        // The field itself contains the array data to display
        buildCustomDatatablePreviewData(value, fieldGroup, field, order);
        break;
      }
      case 'customeditabletable': {
        if (!value || typeof value !== 'object') {
          break;
        }

        const editableTableData = value as Record<string, Record<string, any>>;
        const rows = field.props?.['rows'] || [];
        const columns = field.props?.['columns'] || [];
        const showRowLabels = field.props?.['showRowLabels'] ?? true;
        const rowColumnTitle = field.props?.['rowColumnTitle'] || '';

        fieldGroup.push({
          fieldType: 'customeditabletable',
          fieldBindLabel: '',
          fieldName: field.key as string,
          fieldLabel: field.props?.label || '',
          fieldValue: editableTableData,
          order,
          additionalData: {
            rows,
            columns,
            showRowLabels,
            rowColumnTitle,
          },
        });
        break;
      }
      case 'customtablewithinputform': {
        buildCustomTableWithInputPreviewData(value, fieldGroup, field, order);
        break;
      }
      case 'custommultiselectdatafetch': {
        // Build preview data with metadata for multi-select data fetch
        buildCustomMultiSelectDataFetchPreviewData(
          value,
          fieldGroup,
          field,
          order,
          layoutData,
          section,
          block,
          isNestedBlock
        );
        break;
      }
      case 'radio':
      case 'checkbox':
      case 'multicheckbox':
      case 'select':
      case 'customdropdown':
      case 'customdropdownpaginated':
      case 'customdropdowndatafetchpaginated': {
        if (!value || (value instanceof String && value?.length <= 0)) {
          break;
        }
        // Label lookup when showLabelInSummary is enabled
        if (field.props?.['showLabelInSummary'] === true) {
          const bindLabel = field.props?.['bindLabel'] || 'label';
          let labelExtracted = false;

          // If getSummaryLabelFromValue is true and value is an object, extract label directly
          if (
            field.props?.['getSummaryLabelFromValue'] === true &&
            typeof value === 'object' &&
            !Array.isArray(value)
          ) {
            const valueObj = value as Record<string, unknown>;
            if (valueObj[bindLabel]) {
              value = valueObj[bindLabel];
              labelExtracted = true;
            }
          }

          // Fallback to options lookup if label was not extracted
          if (
            !labelExtracted &&
            (typeof value === 'string' || typeof value === 'number')
          ) {
            const options = field.props?.['options'] as Record<
              string,
              unknown
            >[];
            const bindValue = field.props?.['bindValue'] || 'value';

            if (options && Array.isArray(options)) {
              const matchedOption = options.find(
                opt => opt[bindValue] === value
              );
              if (matchedOption && matchedOption[bindLabel]) {
                value = matchedOption[bindLabel];
              }
            }
          }
        }

        fieldGroup.push({
          fieldType: field.type as string,
          fieldBindLabel: getBindLabel(field),
          fieldName: field.key as string,
          fieldLabel: field?.props?.label as string,
          fieldValue: value,
          order,
        });
        break;
      }
      default:
        if (!value || (value instanceof String && value?.length <= 0)) {
          break;
        }
        if (field.props?.['currencyFormatting']) {
          if (typeof value === 'number') {
            const formattedValue = formatNumberWithCommas(value);
            value = formattedValue;
          }
        }

        fieldGroup.push({
          fieldType: field.type as string,
          fieldBindLabel: getBindLabel(field),
          fieldName: field.key as string,
          fieldLabel: field?.props?.label as string,
          fieldValue: value,
          order,
        });
        break;
    }
  });
  return { fieldGroup, fieldArray };
};

const getBindLabel = (field: FormlyFieldConfig): string | undefined => {
  if (
    !(
      field.type === 'customdropdown' ||
      field.type === 'customdropdownpaginated'
    )
  )
    return '';
  return field?.props?.['bindLabel'];
};

const setCountOnLabel = (setCount = false, index = 0) => {
  return setCount ? `#${index + 1}` : '';
};

const getNestedFields = (
  repeaterField: FormlyFieldConfig,
  addLabelCount: boolean,
  index: number,
  section: FormlyFieldConfig,
  layoutData: Record<string, unknown>,
  order?: number
): IFieldGroupNameData | null => {
  let nestedBlockData: IFieldData[] = [];
  if (repeaterField.fieldGroup) {
    nestedBlockData = getFieldData(
      section,
      repeaterField,
      layoutData,
      addLabelCount,
      true
    ).fieldGroup;
  }

  const fieldGroupData = {
    fieldGroupName: (repeaterField?.props?.label +
      setCountOnLabel(addLabelCount, index)) as string,
    fieldGroupKey: repeaterField.key as string,
    fieldGroup: nestedBlockData,
    order,
  };

  return fieldGroupData.fieldGroup.length > 0 ? fieldGroupData : null;
};

const createLocationFields = (
  configs: Record<string, unknown>[],
  value: unknown,
  field: FormlyFieldConfig,
  fieldParentKey: string,
  order?: number
): IFieldData[] => {
  return configs
    .map(ele => {
      const _value = (<Record<string, unknown>>value)[field.key as string];
      return {
        fieldType: 'customdropdown',
        fieldBindLabel: ele['bindlabel'] as string,
        fieldName: ele['key'] as string,
        fieldLabel: ele['label'] as string,
        fieldParentKey: fieldParentKey,
        fieldValue: _value
          ? (_value as Record<string, unknown>)[ele['key'] as string]
          : '',
        order,
      };
    })
    .filter(x => x.fieldValue);
};

export const getCertificateNameByLocale = (
  certificateNames: Record<string, string>
) => {
  const locale = localStorage.getItem('locale');
  const defaultLocale = 'en-US';
  if (locale) {
    return certificateNames[locale];
  }
  return certificateNames[defaultLocale];
};

/**
 * Get nested value from an object using dot notation
 * @param obj The object to traverse
 * @param path The dot-separated path (e.g., 'user.name')
 * @returns The value at the path or undefined
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;

  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
}

/**
 * Interpolate template variables in a string
 * @param template The template string with {{variable}} placeholders
 * @param context The context object with variable values
 * @returns The interpolated string
 */
function interpolateLabel(
  template: string,
  context: Record<string, any>
): string {
  if (!template || typeof template !== 'string') return template;

  return template.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
    return context[fieldName] || match;
  });
}

/**
 * Extended version of getNestedFields that supports label context for dynamic repeaters
 */
const getNestedFieldsWithContext = (
  repeaterField: FormlyFieldConfig,
  addLabelCount: boolean,
  index: number,
  section: FormlyFieldConfig,
  layoutData: Record<string, unknown>,
  order?: number,
  labelContext?: Record<string, any>
): IFieldGroupNameData | null => {
  let nestedBlockData: IFieldData[] = [];
  if (repeaterField.fieldGroup) {
    // Process nested fields and apply label interpolation
    const rawFieldData = getFieldData(
      section,
      repeaterField,
      layoutData,
      addLabelCount,
      true
    );

    // Apply label interpolation to all field labels if context is provided
    if (labelContext) {
      nestedBlockData = rawFieldData.fieldGroup.map(field => ({
        ...field,
        fieldLabel: interpolateLabel(field.fieldLabel, labelContext),
      }));
    } else {
      nestedBlockData = rawFieldData.fieldGroup;
    }
  }

  const fieldGroupName = labelContext
    ? interpolateLabel(repeaterField?.props?.label as string, labelContext)
    : ((repeaterField?.props?.label +
        setCountOnLabel(addLabelCount, index)) as string);

  const fieldGroupData = {
    fieldGroupName,
    fieldGroupKey: repeaterField.key as string,
    fieldGroup: nestedBlockData,
    order,
  };

  return fieldGroupData.fieldGroup.length > 0 ? fieldGroupData : null;
};

export const removePropertyFromArray = (arr: any[], col: string) => {
  return arr.map(obj => {
    const { [col]: _, ...rest } = obj;
    return rest;
  });
};

export const buildCustomDatatablePreviewData = (
  value: unknown,
  fieldGroup: IFieldData[],
  field: FormlyFieldConfig,
  order: number
) => {
  // Only show table if it has data to avoid rendering issues
  if (Array.isArray(value) && value.length > 0) {
    const fieldData = {
      fieldType: 'customdatatable',
      fieldBindLabel: '',
      fieldName: field.key as string,
      fieldLabel: field?.props?.label as string,
      fieldValue: value,
      order,
      additionalData: removePropertyFromArray(
        field.props?.['columns'],
        'sortable'
      ),
    };
    fieldGroup.push(fieldData);
  }
};

// customtablewithinputform stores its value as a plain array of row records
// (like customdatatable) rather than customeditabletable's row/column matrix
// — reuses the same irembogov-data-table preview rendering, but needs its
// columns reshaped from TableWithInputColumn (key/type/props.label) to the
// {key, title} shape that component expects, and select/checkbox cell values
// resolved to their display text (irembogov-data-table's own row rendering
// then runs each cell through `| translate`, same as every other preview
// table here).
export const buildCustomTableWithInputPreviewData = (
  value: unknown,
  fieldGroup: IFieldData[],
  field: FormlyFieldConfig,
  order: number
) => {
  if (!Array.isArray(value) || value.length === 0) {
    return;
  }

  const columns: TableWithInputColumn[] = field.props?.['columns'] || [];
  const visibleColumns = columns.filter(column => !column.props?.hiddenColumn);
  const tableColumns = visibleColumns.map(column => ({
    key: column.key,
    title: column.props?.label ?? column.key,
  }));

  const rows = value.map((row: Record<string, unknown>) => {
    const formattedRow: Record<string, unknown> = { ...row };
    visibleColumns.forEach(column => {
      const raw = row[column.key];
      const kind = resolveTableWithInputColumnKind(column);
      if (kind === 'checkbox') {
        formattedRow[column.key] = raw ? 'Yes' : 'No';
      } else if (kind === 'select') {
        formattedRow[column.key] = resolveTableWithInputSelectDisplayValues(
          column,
          raw
        ).join(', ');
      }
    });
    return formattedRow;
  });

  fieldGroup.push({
    fieldType: 'customtablewithinputform',
    fieldBindLabel: '',
    fieldName: field.key as string,
    fieldLabel: (field.props?.label as string) || '',
    fieldValue: rows,
    order,
    additionalData: tableColumns,
  });
};

/**
 * Extract value from input metadata configuration
 */
const extractInputMetadataValue = (
  config: any,
  value: unknown[],
  blockData: unknown
): any => {
  const firstItem = value[0] as Record<string, any>;
  const internalInputValues = firstItem?.['__inputValues'];

  if (
    internalInputValues &&
    internalInputValues[config.sourceField] !== undefined
  ) {
    return extractLabelFromValue(
      internalInputValues[config.sourceField],
      config.bindLabel
    );
  }

  const siblingValue = (blockData as Record<string, unknown>)?.[
    config.sourceField
  ];
  return extractLabelFromValue(siblingValue, config.bindLabel);
};

/**
 * Extract label from a value that might be an object with label property
 */
const extractLabelFromValue = (value: unknown, bindLabel?: string): any => {
  if (!value) return null;

  if (typeof value === 'object' && !Array.isArray(value)) {
    const label = bindLabel || 'label';
    return (
      (value as Record<string, unknown>)[label] ||
      (value as Record<string, unknown>)['name'] ||
      (value as Record<string, unknown>)['value'] ||
      value
    );
  }

  return value;
};

/**
 * Compute single metadata value based on config type
 */
const computeMetadataValue = (
  config: any,
  value: unknown[],
  blockData: unknown
): any => {
  switch (config.type) {
    case 'count':
      return value.length;
    case 'sum':
      return value.reduce((sum: number, item: any) => {
        return sum + (getNestedValueFromPath(item, config.sourceField) || 0);
      }, 0);
    case 'first':
      return value.length > 0
        ? getNestedValueFromPath(value[0], config.sourceField)
        : null;
    case 'input':
      return extractInputMetadataValue(config, value, blockData);
    case 'static':
      return config.value;
    default:
      return null;
  }
};

export const buildCustomMultiSelectDataFetchPreviewData = (
  value: unknown,
  fieldGroup: IFieldData[],
  field: FormlyFieldConfig,
  order: number,
  layoutData: Record<string, unknown>,
  section: FormlyFieldConfig,
  block: FormlyFieldConfig,
  isNestedBlock: boolean
) => {
  if (!Array.isArray(value) || value.length === 0) {
    return;
  }

  const previewMetadataConfig = field.props?.['previewMetadata'] || [];
  const metadata: Record<string, any> = {};

  const blockData = isNestedBlock
    ? layoutData
    : (layoutData?.[section?.key as string] as Record<string, unknown>)?.[
        block?.key as string
      ];

  previewMetadataConfig.forEach((config: any) => {
    metadata[config.key] = computeMetadataValue(config, value, blockData);

    if (config.label) {
      metadata[`${config.key}Label`] = config.label;
    }
  });

  const fieldData = {
    fieldType: 'custommultiselectdatafetch',
    fieldBindLabel: '',
    fieldName: field.key as string,
    fieldLabel: field?.props?.label as string,
    fieldValue: value,
    order,
    additionalData: {
      columns: removePropertyFromArray(
        field.props?.['columns'] || [],
        'sortable'
      ),
      metadata: metadata,
      previewMetadataConfig: previewMetadataConfig,
    },
  };
  fieldGroup.push(fieldData);
};

// Helper function to get nested value from path
const getNestedValueFromPath = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

function formatNumberWithCommas(value: any): string {
  return Number(value).toLocaleString('en-US');
}
